import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

/**
 * CurriculumAdmitBadge — compact chip surfacing the modal admit
 * score for the user's selected curriculum on a search-results card.
 *
 * Example renderings:
 *   • "🌐 IB · modal 40"                (Oxford, user picked IB)
 *   • "🇺🇸 SAT · modal 1540"            (Harvard, US selected)
 *   • "🇬🇧 A-Level · tariff shape"     (UK uni + A-Level default)
 *
 * The user's chosen curriculum is stored in localStorage by
 * `GradesComparator`. If no admit band exists for (uni × curriculum),
 * we render nothing (chip is opt-in signal, not required scaffolding).
 *
 * Data is fetched via the bulk `/admit-bands/bulk` endpoint, cached
 * in a module-level `Map` keyed by (curriculum, uni_id_batch) so a
 * page with N cards issues one request, not N.
 */

const _BAND_CACHE = new Map();

/** Fetch bands for a batch of uni_ids at once — memoised per
 *  curriculum + sorted-id-key. */
async function _fetchBandsBulk(uniIds, curriculum) {
  const key = `${curriculum}:${[...uniIds].sort().join(",")}`;
  if (_BAND_CACHE.has(key)) return _BAND_CACHE.get(key);
  const p = api
    .get(`/admit-bands/bulk?curriculum=${encodeURIComponent(curriculum)}&ids=${encodeURIComponent([...uniIds].join(","))}`)
    .then((r) => r.data || {})
    .catch(() => ({ bands: {} }));
  _BAND_CACHE.set(key, p);
  return p;
}


/**
 * Hook that returns the user's currently-selected curriculum id.
 * Falls back to "a_level" when localStorage is empty. Re-reads on
 * every `curriculumchange` window event so switching curriculum
 * in the comparator propagates to every card on the results page.
 */
export function useSelectedCurriculum() {
  const [id, setId] = useState(() => {
    try { return localStorage.getItem("wua_curriculum") || "a_level"; }
    catch { return "a_level"; }
  });
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "wua_curriculum") setId(e.newValue || "a_level");
    };
    const onCustom = (e) => setId(e.detail || "a_level");
    window.addEventListener("storage", onStorage);
    window.addEventListener("wua-curriculum-change", onCustom);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("wua-curriculum-change", onCustom);
    };
  }, []);
  return id;
}


export default function CurriculumAdmitBadge({ universityId, uniIdsInPage }) {
  const curriculum = useSelectedCurriculum();
  const [band, setBand] = useState(null);
  const [meta, setMeta] = useState(null);
  const batch = useMemo(
    () => (uniIdsInPage && uniIdsInPage.length > 0
      ? uniIdsInPage
      : [universityId]),
    [uniIdsInPage, universityId],
  );

  useEffect(() => {
    if (!universityId) return;
    let cancelled = false;
    _fetchBandsBulk(batch, curriculum).then((res) => {
      if (cancelled) return;
      setBand((res.bands || {})[universityId] || null);
      setMeta({
        label: res.curriculum_label,
        flag: res.curriculum_flag,
        curriculum: res.curriculum,
      });
    });
    return () => { cancelled = true; };
  }, [universityId, curriculum, batch]);

  if (!band || !meta) return null;
  const { p50, unit, confidence, direction } = band;
  if (p50 == null) return null;

  const chipLabel = _formatChipLabel(meta.curriculum, p50, unit, direction);
  const testid = `curriculum-admit-badge-${universityId}`;

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-mono ${
        confidence === "official"
          ? "border-emerald-200 bg-emerald-50 text-emerald-800"
          : "border-amber-200 bg-amber-50 text-amber-800"
      }`}
      title={`Modal admit for ${meta.label} · ${band.note || ""}`}
      data-testid={testid}
    >
      <span aria-hidden>{meta.flag}</span>
      <span>{chipLabel}</span>
    </span>
  );
}


function _formatChipLabel(curriculum, p50, unit, direction) {
  const rounded = Math.round(p50 * 100) / 100;
  const suffix = direction === "asc" ? "" : "";
  switch (curriculum) {
    case "ib":       return `IB · modal ${rounded}`;
    case "us":       return `SAT · modal ${rounded}`;
    case "a_level":  return `Modal ${rounded} UCAS`;
    case "in":       return `${rounded}% · modal`;
    case "fr":       return `Bac · modal ${rounded}/20`;
    case "de":       return `Abitur · modal ${rounded}`;
    case "es":       return `EvAU · modal ${rounded}/14`;
    case "cn":       return `Gaokao · modal ${rounded}/750`;
    default:         return `${rounded}${unit ? " " + unit : ""}${suffix}`;
  }
}
